import { Box, Flex, Skeleton, Text, Tooltip, VStack, useOutsideClick } from '@chakra-ui/react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import AddButton from 'components/AddButton';
import HelpComponent from 'components/CustomHelp';
import QuestionMarkIcon from 'components/Icons/QuestionMarkIcon';
import ErrorPage from 'pages/ErrorPage';
import { STATUS } from 'projectConstants';
import { handleExpandSideInput } from 'redux/BuilderModeSlice';
import {
  addDimensionItem,
  fetchDimensionItems,
  fetchIndicatorDetail,
  fetchIndicatorDetailType,
  fetchModelTime,
  geModelTimeStatus,
  getChartFilterItems,
  getDimensionItems,
  getIndicatorDetail,
  getIndicatorDetailStatus,
  resetIndicator,
  setTabShimmer,
} from 'redux/IndicatorPageSlice';
import { getCurrentDimensionIndex } from 'redux/InputPageSlice';
import {
  FetchPlangPageIndicatorTableData,
  onErrorShow,
  getmultiDimFiltersItems,
  getIndicatorTableData,
  getIndicatorTableDataStatus,
  setDriver,
  UpdatePlangPageIndicatorTableData,
} from 'redux/PlanPageSlice';
import {
  FetchDataInput,
  getBaseScenario,
  getExpandValue,
  getSelectedIndicators,
  handleToggleInputPanel,
} from 'redux/PlannerModeSlice';
import addNewDimensionItem from 'utils/AddNewDimItem';
import { AppDispatch } from 'utils/GlobalHelpers';
import { getToolbarIndicatorType } from 'utils/resolveSelectedIndicatorFromMetadata';
import EmptyData from 'pages/BuilderMode/IndicatorPageOPZ/EmptyData';

import { BUILDER_MODE_V1_TOKENS } from '../constants';

import BuilderModeInputTypeTabsV1 from './components/BuilderModeInputTypeTabsV1';
import InputPanelChartSectionV1 from './components/InputPanelChartSectionV1';
import InputPanelChartV1 from './components/InputPanelChartV1';
import InputPanelContainerV1 from './components/InputPanelContainerV1';
import InputPanelHeaderV1 from './components/InputPanelHeaderV1';
import BuilderModeIndicatorTableV1 from './table/BuilderModeIndicatorTableV1';

const BuilderModeIndicatorPageV1 = () => {
  const dispatch = useDispatch<AppDispatch>();
  const currentDriver = useSelector(getSelectedIndicators);
  const containerReference = useRef<HTMLDivElement>(null);
  const [innerHeight, setInnerHeight] = useState(0);
  const indicatorTableData = useSelector(getIndicatorTableData);
  const indicatorTableDataStatus = useSelector(getIndicatorTableDataStatus);
  const indicator = useSelector(getIndicatorDetail);
  const isExpand = useSelector(getExpandValue);
  const filteredChartItems = useSelector(getChartFilterItems);
  const multiDimFiltersItems = useSelector(getmultiDimFiltersItems);

  useLayoutEffect(() => {
    if (containerReference.current) {
      const containerHeight = containerReference.current.offsetHeight;
      const containerPadding =
        Number.parseInt(window.getComputedStyle(containerReference.current).paddingLeft, 10) +
        Number.parseInt(window.getComputedStyle(containerReference.current).paddingRight, 10);
      setInnerHeight(containerHeight - containerPadding);
    }
  }, [containerReference.current]);

  const dimensionItems = useSelector(getDimensionItems);
  const { pathname } = useLocation();
  const { modelId, blockId } = useParams();
  const indicatorDetailStatus = useSelector(getIndicatorDetailStatus);
  const modelTimeStatus = useSelector(geModelTimeStatus);
  const currentDimensionIndex = useSelector(getCurrentDimensionIndex);
  const baseScenario = useSelector(getBaseScenario);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [isChartRefreshing, setIsChartRefreshing] = useState(false);
  const [isTableRefreshing, setIsTableRefreshing] = useState(false);
  const tooltipReference = useRef<HTMLDivElement>(null);

  const chartFilterDimId = indicatorTableData?.column_headers?.[0]?.is_dimension
    ? indicatorTableData.column_headers[0].id
    : null;

  const dimensionId = currentDriver?.data_input?.dimensions?.[currentDimensionIndex];
  const isInputDriver = getToolbarIndicatorType(currentDriver) === 'input';

  const tableMatchesDriver =
    currentDriver?.id != null &&
    indicatorTableData != null &&
    Number(indicatorTableData?.indicator_id ?? currentDriver.id) === Number(currentDriver.id);

  const hasIndicatorTablePayload =
    indicatorTableData != null &&
    ((indicatorTableData?.column_headers?.length ?? 0) > 0 ||
      (indicatorTableData?.excel_data?.length ?? 0) > 0 ||
      indicatorTableData?.data_values != null ||
      indicatorTableData?.type != null);

  useOutsideClick({
    ref: tooltipReference,
    handler: () => setIsTooltipOpen(false),
  });

  const previousDriverIdReference = useRef<number | null>(null);
  const skipTypeChartRefetchReference = useRef(false);

  useEffect(() => {
    setIsChartRefreshing(false);
    setIsTableRefreshing(false);
  }, [currentDriver?.id]);

  useEffect(() => {
    if (!currentDriver?.id) return;

    if (
      previousDriverIdReference.current != null &&
      previousDriverIdReference.current !== currentDriver.id
    ) {
      dispatch(resetIndicator());
    }
    previousDriverIdReference.current = currentDriver.id;
    dispatch(setDriver(currentDriver));
  }, [currentDriver?.id, dispatch]);

  useEffect(() => {
    if (modelId && blockId) {
      dispatch(fetchModelTime(Number(modelId)));
    }
  }, [pathname, currentDriver?.id, modelId, blockId, dispatch]);

  useEffect(() => {
    if (currentDriver?.data_input?.dimensions?.length > 0) {
      dispatch(
        fetchDimensionItems({
          dimensionId,
          parameters: {
            scenario_id: baseScenario?.id,
          },
        }),
      );
    }
  }, [pathname, currentDriver?.id, baseScenario?.id, dimensionId, dispatch]);

  // Table API — fires immediately on land / driver change (parallel with chart)
  useEffect(() => {
    if (!modelId || !blockId || !isInputDriver || !baseScenario?.id || !currentDriver?.id) {
      return;
    }

    dispatch(setDriver(currentDriver));
    dispatch(
      FetchPlangPageIndicatorTableData({
        indicator_id: currentDriver.id,
        parameters: {
          scenario_id: baseScenario.id,
          format_excel_data: true,
        },
      }),
    );
  }, [currentDriver?.id, baseScenario?.id, blockId, modelId, isInputDriver, dispatch]);

  // Chart API — fires immediately on land / driver change (parallel with table)
  useEffect(() => {
    if (!modelId || !blockId || !isInputDriver || !baseScenario?.id || !currentDriver?.id) {
      return;
    }

    const dimId =
      currentDriver?.data_input?.dimensions?.[currentDimensionIndex] ??
      currentDriver?.data_input?.dimensions?.[0] ??
      null;

    dispatch(
      fetchIndicatorDetail({
        currentDriver,
        parameters: {
          dim_id: dimId,
          scenario_id: baseScenario.id,
          is_chart_show: true,
        },
      }),
    );
  }, [
    currentDriver?.id,
    currentDriver?.data_input?.dimensions,
    currentDimensionIndex,
    baseScenario?.id,
    blockId,
    modelId,
    isInputDriver,
    dispatch,
  ]);

  // Refine chart dim once table headers arrive (keeps chart visible if already loaded)
  useEffect(() => {
    if (
      !chartFilterDimId ||
      !currentDriver?.id ||
      !baseScenario?.id ||
      !isInputDriver ||
      !tableMatchesDriver
    ) {
      return;
    }

    dispatch(
      fetchIndicatorDetail({
        currentDriver,
        parameters: {
          dim_id: chartFilterDimId,
          scenario_id: baseScenario.id,
          is_chart_show: true,
        },
      }),
    );
  }, [chartFilterDimId, currentDriver?.id, baseScenario?.id, isInputDriver, dispatch]);

  const tableRetryCountReference = useRef<Record<number, number>>({});

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    if (!currentDriver?.id || !baseScenario?.id || !isInputDriver) {
      return () => {
        if (timer) clearTimeout(timer);
      };
    }

    const driverId = Number(currentDriver.id);
    const tableIndicatorId = Number(indicatorTableData?.indicator_id);

    if (tableIndicatorId === driverId && (indicatorTableData?.column_headers?.length ?? 0) === 0) {
      const retries = tableRetryCountReference.current[driverId] ?? 0;
      if (retries < 4) {
        timer = setTimeout(() => {
          tableRetryCountReference.current[driverId] = retries + 1;
          dispatch(
            FetchPlangPageIndicatorTableData({
              indicator_id: currentDriver.id,
              parameters: {
                scenario_id: baseScenario.id,
                format_excel_data: true,
              },
            }),
          );
        }, 600 * (retries + 1));
      }
    } else if ((indicatorTableData?.column_headers?.length ?? 0) > 0) {
      delete tableRetryCountReference.current[driverId];
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [
    currentDriver?.id,
    baseScenario?.id,
    indicatorTableData?.indicator_id,
    indicatorTableData?.column_headers?.length,
    isInputDriver,
    dispatch,
  ]);

  const [noDimChartData, setNoDimChartData] = useState<Record<string, Record<string, number>> | []>(
    [],
  );

  useEffect(() => {
    if (
      isChartRefreshing ||
      isTableRefreshing ||
      !multiDimFiltersItems ||
      multiDimFiltersItems.length === 0
    ) {
      return;
    }

    if (skipTypeChartRefetchReference.current) {
      skipTypeChartRefetchReference.current = false;
      return;
    }

    dispatch(
      fetchIndicatorDetailType({
        currentDriver: indicator,
        parameters: {
          dim_id: chartFilterDimId,
          scenario_id: baseScenario?.id,
          is_chart_show: true,
          filter_dim_items: JSON.stringify(filteredChartItems),
        },
      }),
    ).then(() => {
      dispatch(
        fetchIndicatorDetail({
          currentDriver: indicator,
          parameters: {
            dim_id: chartFilterDimId,
            scenario_id: baseScenario?.id,
            is_chart_show: true,
            filter_dim_items: JSON.stringify(filteredChartItems),
          },
        }),
      );
    });
  }, [indicatorTableData?.type, isChartRefreshing, isTableRefreshing]);

  useEffect(() => {
    if (indicator && !indicator?.output?.dims) {
      const object: Record<string, Record<string, number>> = {};
      object[indicator.output?.name] = indicator.output?.total_values;
      setNoDimChartData(object);
    }
  }, [indicator]);

  useEffect(() => {
    if (indicatorDetailStatus === STATUS.FAILED || modelTimeStatus === STATUS.FAILED) {
      dispatch(onErrorShow(true));
    }
  }, [indicatorDetailStatus, modelTimeStatus, dispatch]);

  const closeSection = () => {
    dispatch(handleToggleInputPanel());
    dispatch(handleExpandSideInput(false));
  };

  const chartMatchesDriver =
    currentDriver?.id != null &&
    indicator != null &&
    Number(indicator.id ?? indicator.output?.id) === Number(currentDriver.id);

  const hasChartData = chartMatchesDriver && Boolean(indicator?.output);
  const hasTableContent = tableMatchesDriver && hasIndicatorTablePayload;

  const showChartLoading =
    isInputDriver &&
    indicatorDetailStatus !== STATUS.FAILED &&
    (isChartRefreshing || indicatorDetailStatus === STATUS.LOADING || !hasChartData);

  const showTableLoading =
    isInputDriver &&
    indicatorTableDataStatus !== STATUS.FAILED &&
    (isTableRefreshing || indicatorTableDataStatus === STATUS.LOADING || !hasTableContent);

  const isInputTypeChangeBusy = isChartRefreshing || isTableRefreshing;

  const refetchData = useCallback(async () => {
    if (!currentDriver?.id || !baseScenario?.id) return;

    setIsChartRefreshing(true);
    setIsTableRefreshing(true);
    dispatch(setTabShimmer(true));

    const chartRequest = dispatch(
      fetchIndicatorDetail({
        currentDriver,
        parameters: {
          dim_id: chartFilterDimId,
          scenario_id: baseScenario.id,
          is_chart_show: true,
        },
      }),
    )
      .unwrap()
      .finally(() => setIsChartRefreshing(false));

    const tableRequest = dispatch(
      FetchPlangPageIndicatorTableData({
        indicator_id: currentDriver.id,
        parameters: {
          scenario_id: baseScenario.id,
          format_excel_data: true,
        },
      }),
    )
      .unwrap()
      .finally(() => setIsTableRefreshing(false));

    try {
      await Promise.all([chartRequest, tableRequest]);
    } finally {
      dispatch(setTabShimmer(false));
    }
  }, [baseScenario?.id, chartFilterDimId, currentDriver, dispatch]);

  const handleInputTypeChange = useCallback(
    async (newType: string) => {
      if (!currentDriver?.id || !baseScenario?.id) return;

      setIsChartRefreshing(true);
      setIsTableRefreshing(true);
      dispatch(setTabShimmer(true));
      try {
        await dispatch(
          UpdatePlangPageIndicatorTableData({
            indicator_id: currentDriver.id,
            request_body: {
              type: newType,
              scenario_id: baseScenario.id,
              bulk_input: true,
              format_excel_data: true,
            },
          }),
        ).unwrap();

        skipTypeChartRefetchReference.current = true;

        const chartRequest = dispatch(
          fetchIndicatorDetail({
            currentDriver,
            parameters: {
              dim_id: chartFilterDimId,
              scenario_id: baseScenario.id,
              is_chart_show: true,
            },
          }),
        )
          .unwrap()
          .finally(() => setIsChartRefreshing(false));

        const tableRequest = dispatch(
          FetchPlangPageIndicatorTableData({
            indicator_id: currentDriver.id,
            parameters: {
              scenario_id: baseScenario.id,
              format_excel_data: true,
            },
          }),
        )
          .unwrap()
          .finally(() => setIsTableRefreshing(false));

        await Promise.all([chartRequest, tableRequest]);
      } catch {
        setIsChartRefreshing(false);
        setIsTableRefreshing(false);
      } finally {
        dispatch(setTabShimmer(false));
      }
    },
    [baseScenario?.id, chartFilterDimId, currentDriver, dispatch],
  );

  const renderChartShimmer = () => (
    <Box
      h={innerHeight ? `${innerHeight}px` : '25vh'}
      minH='120px'
      maxH='190px'
      flexShrink={0}
      w='100%'
    >
      <Skeleton
        startColor='rgba(139, 92, 246, 0.08)'
        endColor='rgba(139, 92, 246, 0.2)'
        h='100%'
        borderRadius='8px'
      />
    </Box>
  );

  const renderTableShimmer = () => (
    <Box
      w='100%'
      flexShrink={0}
      border='1px solid'
      borderColor={BUILDER_MODE_V1_TOKENS.border}
      borderRadius='12px'
      bg='white'
      p='12px'
    >
      <Flex direction='column' gap='10px'>
        <Skeleton
          startColor='rgba(139, 92, 246, 0.08)'
          endColor='rgba(139, 92, 246, 0.2)'
          h='44px'
          borderRadius='10px'
        />
        {[...new Array(5)].map((_, index) => (
          <Skeleton
            key={index}
            startColor='rgba(139, 92, 246, 0.08)'
            endColor='rgba(139, 92, 246, 0.2)'
            h='40px'
            borderRadius='8px'
          />
        ))}
      </Flex>
    </Box>
  );

  if (getToolbarIndicatorType(currentDriver) === 'calculation' || !currentDriver) {
    return (
      <InputPanelContainerV1 className='input-panel visible'>
        <InputPanelHeaderV1 title={indicator?.output?.name || 'Inputs'} onClose={closeSection} />
        <Flex flex={1} align='center' justify='center'>
          <EmptyData />
        </Flex>
      </InputPanelContainerV1>
    );
  }

  const onAddDimensionItem = async () => {
    await dispatch(
      addDimensionItem({
        dimension_id: currentDriver?.data_input?.dimensions?.[currentDimensionIndex],
        request_body: addNewDimensionItem(dimensionItems?.items, baseScenario?.id),
        toaster: {
          successMessage: 'Successfully added',
          errorMessage: 'An item with this name already exists',
        },
      }),
    );
    await dispatch(
      FetchDataInput({
        indicator_id: currentDriver?.id,
        parameters: {
          scenario_id: baseScenario?.id,
          format_excel_data: true,
        },
      }),
    );
    await refetchData();
  };

  return (
    <InputPanelContainerV1 className='input-panel visible'>
      <InputPanelHeaderV1
        title={indicator?.output?.name || currentDriver?.name || 'Inputs'}
        onClose={closeSection}
      />

      {indicator?.help && (
        <Tooltip
          isOpen={isTooltipOpen}
          hasArrow
          arrowSize={10}
          bg='white'
          placement='right'
          boxShadow='rgba(0, 0, 0, 0.24) 0px 3px 8px'
          label={
            <VStack bg='white' p='3' maxW='300px' w='180px' gap={1}>
              <Text fontFamily='bold_roboto' fontSize='sm' color='black'>
                Help
              </Text>
              <Text fontSize='2xs' color='black' noOfLines={3}>
                {indicator.help}
              </Text>
            </VStack>
          }
        >
          <Box
            ref={tooltipReference}
            position='absolute'
            top='24px'
            left='24px'
            display='none'
            onClick={() => setIsTooltipOpen(!isTooltipOpen)}
          >
            <QuestionMarkIcon height={23} width={23} color={BUILDER_MODE_V1_TOKENS.textDark} />
          </Box>
        </Tooltip>
      )}

      <Flex direction='column' gap='20px' flex={1} minH={0} overflow='hidden' h='100%'>
        {showChartLoading ? (
          renderChartShimmer()
        ) : indicatorDetailStatus === STATUS.FAILED ? (
          <Flex
            h={innerHeight ? `${innerHeight}px` : '25vh'}
            minH='120px'
            maxH='190px'
            flexShrink={0}
            align='center'
            justify='center'
          >
            <ErrorPage style={{ height: '100%' }} planError />
          </Flex>
        ) : (
          <InputPanelChartSectionV1>
            <Box ref={containerReference} w='100%' h='100%'>
              {indicator?.output?.dims ? (
                <InputPanelChartV1
                  showLabel
                  chartLabelData={indicator.output.total_values}
                  chartData={indicator.output?.dim_item_data}
                  chartType={indicator.output?.instrument_type}
                  dimId={indicator.output?.dims?.id}
                  formatProperties={indicator.output?.data_format_properties}
                />
              ) : (
                <InputPanelChartV1
                  showLabel
                  chartLabelData={indicator?.output?.total_values}
                  chartData={noDimChartData}
                  chartType={indicator?.output?.instrument_type}
                  formatProperties={indicator?.output?.data_format_properties}
                />
              )}
            </Box>
          </InputPanelChartSectionV1>
        )}

        {isInputDriver && indicatorTableData?.type && (
          <BuilderModeInputTypeTabsV1
            inputType={indicatorTableData.type}
            indicatorId={indicator?.id ?? currentDriver.id}
            isRefreshing={isInputTypeChangeBusy}
            onInputTypeChange={handleInputTypeChange}
          />
        )}

        {isInputDriver && showTableLoading && renderTableShimmer()}

        {isInputDriver && !showTableLoading && indicatorTableDataStatus === STATUS.FAILED && (
          <Flex flex={1} minH='120px' align='center' justify='center'>
            <ErrorPage style={{ height: '100%' }} planError />
          </Flex>
        )}

        {isInputDriver && !showTableLoading && indicatorTableDataStatus !== STATUS.FAILED && (
          <Flex direction='column' flex={1} minH={0} overflow='hidden' w='100%'>
            <BuilderModeIndicatorTableV1
              refetchRequest={refetchData}
              isRefreshing={isInputTypeChangeBusy}
              isPercentage={indicator?.data_format?.toLowerCase()}
            />
            {dimensionItems?.items && isExpand && <AddButton onClick={onAddDimensionItem} />}
            {indicatorTableData?.help && isExpand && (
              <Box mt={4} pb={2}>
                <HelpComponent helpText={indicatorTableData.help} style={{ width: '100%' }} />
              </Box>
            )}
          </Flex>
        )}
      </Flex>
    </InputPanelContainerV1>
  );
};

export default BuilderModeIndicatorPageV1;
